import { useEffect, useState } from "react";
import { api, post } from "./api";
import type { AgentSchedule, EvolutionPlan } from "./types";

const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function oneHourAfter(value?: string) {
  const match = value?.match(/^(\d{2}):(\d{2})$/);
  if (!match) return "10:00";
  return `${String((Number(match[1]) + 1) % 24).padStart(2, "0")}:${match[2]}`;
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

export function EvolutionPlanner({ schedule, gameName, plan, onPlansChanged, onSchedulesChanged, onNotice }: {
  schedule: AgentSchedule;
  gameName: string;
  plan?: EvolutionPlan;
  onPlansChanged: () => Promise<void>;
  onSchedulesChanged: () => Promise<void>;
  onNotice: (message: string | undefined) => void;
}) {
  const [requestOpen, setRequestOpen] = useState(false);
  const [game, setGame] = useState(gameName);
  const [packageName, setPackageName] = useState("");
  const [objective, setObjective] = useState(
    "Build a balanced free-to-play progression plan that improves the account efficiently."
  );
  const [region, setRegion] = useState("");
  const [allowResources, setAllowResources] = useState(true);
  const [researching, setResearching] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState(oneHourAfter(schedule.time_of_day));
  const [weekdays, setWeekdays] = useState<number[]>([1, 4]);
  const [approving, setApproving] = useState(false);
  const [running, setRunning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setGame(plan?.game || gameName);
    setPackageName(plan?.package_name || "");
    setObjective(plan?.objective || "Build a balanced free-to-play progression plan that improves the account efficiently.");
    setRegion(plan?.region || "");
    setAllowResources(plan?.allow_ordinary_resource_spending ?? true);
    setTimeOfDay(plan?.preferred_time || oneHourAfter(schedule.time_of_day));
  }, [gameName, plan, schedule.time_of_day]);

  const research = async () => {
    if (!game.trim() || !objective.trim()) return;
    setResearching(true); onNotice(undefined);
    try {
      const created = await post<EvolutionPlan>(
        `/agent/schedules/${schedule.id}/evolution-plan`,
        {
          game: game.trim(),
          packageName: packageName.trim() || null,
          objective: objective.trim(),
          region: region.trim(),
          timezone: schedule.timezone,
          preferredTime: timeOfDay,
          allowOrdinaryResourceSpending: allowResources,
        },
      );
      setRequestOpen(false);
      await onPlansChanged();
      onNotice(`Evolution research for “${created.game}” is ready for review.`);
    } catch (error) {
      onNotice(errorMessage(error));
    } finally {
      setResearching(false);
    }
  };

  const approve = async () => {
    if (!plan || weekdays.length === 0) return;
    setApproving(true); onNotice(undefined);
    try {
      await post(`/agent/evolution-plans/${plan.id}/approve`, {
        timeOfDay,
        weekdays,
        enabled: true,
        maxActions: 180,
        timeoutSeconds: 1800,
      });
      await Promise.all([onPlansChanged(), onSchedulesChanged()]);
      onNotice(`“${plan.game} Evolution” was approved and scheduled.`);
    } catch (error) {
      onNotice(errorMessage(error));
    } finally {
      setApproving(false);
    }
  };

  const runNext = async () => {
    if (!plan) return;
    setRunning(true); onNotice(undefined);
    try {
      await post(`/agent/evolution-plans/${plan.id}/run-next`);
      await onSchedulesChanged();
      onNotice(`The next approved “${plan.game}” evolution session was queued.`);
    } catch (error) {
      onNotice(errorMessage(error));
    } finally {
      setRunning(false);
    }
  };

  const removePlan = async () => {
    if (!plan || !window.confirm(`Delete the evolution research for “${plan.game}”?`)) return;
    setDeleting(true); onNotice(undefined);
    try {
      await api(`/agent/evolution-plans/${plan.id}`, { method: "DELETE" });
      await onPlansChanged();
      onNotice(`Evolution research for “${plan.game}” was deleted.`);
    } catch (error) {
      onNotice(errorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  return <section className="evolution-tools">
    <div className="evolution-toolbar">
      <div><strong>Evolution orchestrator</strong><small>Progression research linked to this daily routine</small></div>
      <button onClick={() => setRequestOpen((current) => !current)}>
        {plan ? "Research updated plan" : "Generate evolution plan"}
      </button>
    </div>

    {requestOpen && <div className="evolution-request">
      <div className="evolution-request-grid">
        <label><span>Game</span><input aria-label="Evolution game" value={game} maxLength={120} onChange={(event) => setGame(event.target.value)} /></label>
        <label><span>Android package (optional)</span><input aria-label="Evolution Android package" value={packageName} placeholder="com.example.game" onChange={(event) => setPackageName(event.target.value)} /></label>
        <label><span>Region / server</span><input value={region} maxLength={120} placeholder="Optional" onChange={(event) => setRegion(event.target.value)} /></label>
        <label><span>Preferred evolution time</span><input type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} /></label>
      </div>
      <label><span>Progression objective</span><textarea aria-label="Evolution objective" value={objective} maxLength={2000} rows={3} onChange={(event) => setObjective(event.target.value)} /></label>
      <label className="resource-permission"><input type="checkbox" checked={allowResources} onChange={(event) => setAllowResources(event.target.checked)} /><span>Allow ordinary renewable resources for reviewed milestones</span></label>
      <small>Premium currency, money, ads, rare consumables, competitive actions, and account changes always remain blocked.</small>
      <div className="evolution-request-actions"><button onClick={() => setRequestOpen(false)}>Cancel</button><button className="primary" disabled={researching || !game.trim() || !objective.trim()} onClick={research}>{researching ? "Researching current progression…" : "Research evolution plan"}</button></div>
    </div>}

    {plan && <details className="evolution-plan" open>
      <summary><div><strong>{plan.game} evolution plan</strong><small>{plan.status === "approved" ? "Approved" : "Review required"} · researched {new Date(plan.researched_at * 1000).toLocaleString()}</small></div><span>⌄</span></summary>
      <div className="evolution-plan-body">
        <p>{plan.summary}</p>
        <div className="evolution-principles"><strong>Strategy principles</strong><ul>{plan.strategy_principles.map((item, index) => <li key={index}>{item}</li>)}</ul></div>
        <div className="milestone-list">{plan.milestones.map((milestone, index) => <article key={`${milestone.priority}-${index}`} className={milestone.autonomous ? "autonomous" : "review-only"}>
          <header><div><strong>{milestone.priority}. {milestone.title}</strong><small>{milestone.autonomous ? "Approved automation candidate" : "Review only"}</small></div><span className={`risk-${milestone.risk}`}>{milestone.risk} risk</span></header>
          <p>{milestone.rationale}</p>
          {milestone.prerequisites.length > 0 && <small><b>Prerequisites:</b> {milestone.prerequisites.join("; ")}</small>}
          {milestone.actions.length > 0 && <ul>{milestone.actions.map((action, actionIndex) => <li key={actionIndex}>{action}</li>)}</ul>}
          <small><b>Verify:</b> {milestone.completion_signals.join("; ")}</small>
        </article>)}</div>
        {plan.caveats.length > 0 && <details className="evolution-caveats"><summary>Assumptions and exclusions <span>{plan.caveats.length}</span></summary><ul>{plan.caveats.map((item, index) => <li key={index}>{item}</li>)}</ul></details>}
        <div className="research-sources"><strong>Sources used</strong>{plan.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><span>{source.title}</span><small>{source.note}</small></a>)}</div>

        {!plan.evolution_schedule_id ? <div className="evolution-approval">
          <div><label><span>Run time</span><input aria-label="Evolution run time" type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} /></label><div className="evolution-weekdays">{weekdayLabels.map((label, day) => <button key={label} type="button" className={weekdays.includes(day) ? "selected" : ""} onClick={() => setWeekdays((current) => current.includes(day) ? current.filter((item) => item !== day) : [...current, day].sort())}>{label}</button>)}</div></div>
          <small>Runs at low priority and waits when a daily routine is approaching.</small>
          <button className="approve-evolution" disabled={approving || weekdays.length === 0} onClick={approve}>{approving ? "Creating linked schedule…" : "Approve & schedule evolution"}</button>
          <button className="delete-evolution" disabled={deleting} onClick={removePlan}>Delete research</button>
        </div> : <div className="evolution-approved">
          <div><span>✓</span><p><strong>Linked evolution schedule active</strong><small>One bounded milestone per run; daily routines retain priority.</small></p></div>
          <button disabled={running} onClick={runNext}>{running ? "Queueing…" : "Run next milestone now"}</button>
        </div>}
      </div>
    </details>}
  </section>;
}
